// @ts-strict
import { DocumentType, modelOptions, mongoose, PropType, post, pre, prop, type Ref } from "@typegoose/typegoose";
import { z } from "zod";
import { AssetType } from "../../Configurations/AssetTypeConfiguration/AssetTypeConfiguration.js";
import { RootLogger } from "../../logging/index.js";
import { PinoModules } from "../../utils/index.js";
import {
    ENTITY_NAME_MAX_LENGTH,
    entityNameZodValidator,
    ObjectIdString,
    VerdiIdString,
} from "../../validators/index.js";
import { getZodSchemaFromClass, validate } from "../../zod-decorator/index.js";
import { assetIdentitySync } from "../assetIdentity/assetIdentitySyncHook.js";
import { verdiIdGeneration } from "../assetIdentity/verdiIdGenerationHook.js";
import { VertexInfo } from "../commonSubSchemas/vertexInfo.js";
import type { Organization } from "../organization/organization.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, type SchemaConstructorArgs } from "../schemaBase.js";
import { SensorReadingConfiguration } from "../sensorReadingConfiguration/sensorReadingConfiguration.js";
import { SiteFieldsWithDefaults, SiteSchemaTypes } from "../types/site.d.js";
import { type DtoValidatorTypes } from "../validatableClass.js";
import * as getters from "./getters.js";
import * as utilities from "./utilities.js";

const logger = RootLogger.child({ module: PinoModules.models.assetIdentity });

const SCHEMA_KEY = "AreaOfInterest" as const;
export const SITE_COLLECTION_NAME = "areaofinterests" as const;

// Generates verdiId on "validate" rather than "save" since verdiId is a required field
@pre<Site>("validate", async function (this: DocumentType<Site>) {
    if (this.verdiId) return;
    try {
        this.verdiId = await verdiIdGeneration.function(AssetType.AREA_OF_INTEREST, { relatedObjectId: this._id });
    } catch (e) {
        logger.error({ error: e, areaOfInterestId: this._id }, "could not generate verdiId for area of interest");
        throw e;
    }
})
@post<Site>("save", async function (this: DocumentType<Site>) {
    if (!this.verdiId) return;
    // TODO: Once AssetIdentity becomes the source of truth for asset IDs, this should hard-fail instead of
    // catching. We swallow errors here only because the transition is incomplete — site saves
    // must not break if the sync fails during this interim period.
    await assetIdentitySync
        .function({
            assetCollection: SITE_COLLECTION_NAME,
            assetType: AssetType.AREA_OF_INTEREST,
            verdiId: this.verdiId,
        })
        .catch((e) =>
            logger.warn({ error: e, verdiId: this.verdiId }, "could not sync asset identity for area of interest"),
        );
})
@modelOptions({
    schemaOptions: {
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        collection: SITE_COLLECTION_NAME,
        timestamps: true,
        _id: true,
    },
    // Model name is persisted in ref strings; do not remove.
    options: { customName: "AreaOfInterest" },
})
@post<SchemaBase<Site>>(...addSchemaKeyHook(SCHEMA_KEY))
export class Site extends SchemaBase<Site, SiteFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({
        zodValidator: ObjectIdString,
        responseOnly: true,
    })
    public _id!: mongoose.Types.ObjectId;

    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public createdAt?: Date;

    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public updatedAt?: Date;

    @validate({ zodValidator: entityNameZodValidator })
    @prop({ type: () => String, required: true, maxlength: ENTITY_NAME_MAX_LENGTH })
    public name!: string;

    @validate({
        childClass: VertexInfo,
        childClassTransform: (object) => z.array(z.array(object)),
    })
    @prop({ type: () => [[VertexInfo]], required: true }, PropType.ARRAY)
    public polygons!: VertexInfo[][];

    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: true, default: new Date() })
    public created: Date = new Date();

    @validate({ zodValidator: z.boolean(), responseOnly: true })
    @prop({ type: () => Boolean, required: true, default: false })
    public deleted: boolean = false;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    @prop({ type: () => mongoose.Types.ObjectId, required: true, ref: "Organization" })
    public owner!: Ref<Organization>;

    @validate({ zodValidator: VerdiIdString, responseOnly: true })
    @prop({ type: () => String, required: true, unique: true })
    public verdiId!: string;

    @validate({
        childClass: SensorReadingConfiguration,
        childClassTransform: (object) => object.optional(),
    })
    @prop({ type: () => SensorReadingConfiguration, required: false })
    public sensorReadingConfiguration?: SensorReadingConfiguration;

    static getters = { ...getters } as const;

    static utilities = { ...utilities } as const;

    static dtoValidators: DtoValidatorTypes<SiteSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<Site, SiteFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

Site.dtoValidators = getZodSchemaFromClass(Site) as unknown as DtoValidatorTypes<SiteSchemaTypes>;
